/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { addMonths, isBefore, isValid, startOfDay } from 'date-fns';
import { DateUtils } from './dateUtils';

import { describeError } from './describeError';
export function isEmpty(value: string): boolean {
  return !value.trim();
}

/**
 * Conceals a phone number by replacing the first 4 characters with "xxxx"
 * @param phoneNumber - The phone number string to conceal
 * @returns The concealed phone number string
 */
export function getConcealedPhoneNumberString(phoneNumber: string): string {
  if (phoneNumber.length > 4) {
    return 'xxxx' + phoneNumber.substring(4);
  }
  return phoneNumber;
}

export type MaskEmailStyle = 'default' | 'otp' | 'contact';

export type MaskEmailOptions = {
  style?: MaskEmailStyle;
};

/**
 * Masks an email for display.
 * - `default`: first + last char of local and of full domain (e.g. t**t@e*********m)
 * - `otp`: first char + fixed ***** on local and domain name, TLD kept (e.g. d*****@a*****.com)
 * - `contact`: first + last char with fixed ***** on local and *** on domain name, TLD kept (e.g. d*****l@a***a.com)
 */
export function maskEmail(emailAddress?: string, options?: MaskEmailOptions) {
  if (!emailAddress) return '';
  const [localPart, domain] = emailAddress.split('@');
  if (!localPart || !domain) return emailAddress;

  if (options?.style === 'otp') {
    const fixedMask = '*****';
    const lastDot = domain.lastIndexOf('.');
    const domainName = lastDot > 0 ? domain.slice(0, lastDot) : domain;
    const tld = lastDot > 0 ? domain.slice(lastDot) : '';
    return `${localPart.charAt(0)}${fixedMask}@${domainName.charAt(0)}${fixedMask}${tld}`;
  }

  if (options?.style === 'contact') {
    const lastDot = domain.lastIndexOf('.');
    const domainName = lastDot > 0 ? domain.slice(0, lastDot) : domain;
    const tld = lastDot > 0 ? domain.slice(lastDot) : '';

    const maskEnds = (value: string, mask: string) =>
      value.length > 1
        ? `${value.charAt(0)}${mask}${value.charAt(value.length - 1)}`
        : `${value}${mask}`;

    return `${maskEnds(localPart, '*****')}@${maskEnds(domainName, '***')}${tld}`;
  }

  const maskedLocal =
    localPart.length > 2
      ? `${localPart.charAt(0)}${'*'.repeat(localPart.length - 2)}${localPart.charAt(localPart.length - 1)}`
      : localPart;

  const maskedDomain =
    domain.length > 2
      ? `${domain.charAt(0)}${'*'.repeat(domain.length - 2)}${domain.charAt(domain.length - 1)}`
      : domain;

  return `${maskedLocal}@${maskedDomain}`;
}

export type MaskMobileNumberStyle = 'default' | 'verification';

export type MaskMobileNumberOptions = {
  style?: MaskMobileNumberStyle;
  dialingCode?: string;
};

export function maskMobileNumber(
  number?: string | number | null,
  options?: MaskMobileNumberOptions,
): string {
  if (number === undefined || number === null) return '';

  if (options?.style === 'verification') {
    const digits = String(number).replace(/\D/g, '');
    if (!digits) return '';
    const last3 = digits.slice(-3);
    const dialingCode = options.dialingCode;
    const code = !dialingCode
      ? ''
      : dialingCode.startsWith('+')
        ? dialingCode
        : `+${dialingCode}`;
    return `${code}${'*'.repeat(8)}${last3}`;
  }

  const numberStr = String(number);
  if (numberStr.length <= 2) return numberStr;
  return `${numberStr.charAt(0)}${'*'.repeat(numberStr.length - 2)}${numberStr.charAt(numberStr.length - 1)}`;
}

export function getFullMobileNumber(
  mobileNumber: string | undefined,
  dialingCode: string | undefined,
) {
  if (!mobileNumber) return '';
  const dialingCodeStr = dialingCode || '';
  return `${dialingCodeStr}${maskMobileNumber(mobileNumber)}`;
}

export function maskNationalId(id?: string) {
  if (!id) return '-';
  if (id.length <= 2) return id;
  return `${id.charAt(0)}${'*'.repeat(id.length - 2)}${id.charAt(id.length - 1)}`;
}

export function maskPassport(passportNumber?: string) {
  if (!passportNumber) return '-';
  if (passportNumber.length <= 2) return passportNumber;
  return `${passportNumber.charAt(0)}${'*'.repeat(passportNumber.length - 2)}${passportNumber.charAt(passportNumber.length - 1)}`;
}

/**
 * Get expiry status for a date in ddMMyyyy format
 * @param expiryDate - Date string in ddMMyyyy format (e.g., "19112038") or other formats
 * @returns 'expired' if date is past, 'expiring' if within 6 months, null otherwise
 */
export function getExpiryStatus(expiryDate?: string) {
  if (!expiryDate || expiryDate === 'No expiry') return null;

  try {
    let expiry: Date | null = null;

    // First, try parsing as ddMMyyyy format (8 digits)
    expiry = DateUtils.parseDDMMYYYY(expiryDate);

    if (!expiry || !isValid(expiry)) {
      return null;
    }

    const today = startOfDay(new Date());
    const sixMonthsFromNow = addMonths(today, 6);

    // Check if expired (expiry date is before today)
    if (isBefore(expiry, today)) {
      return 'expired';
    }

    // Check if expiring within 6 months
    if (
      isBefore(expiry, sixMonthsFromNow) ||
      expiry.getTime() === sixMonthsFromNow.getTime()
    ) {
      return 'expiring';
    }

    return null;
  } catch (error) {
    console.error('Error parsing expiry date:', describeError(error));
    return null;
  }
}

export const validateEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone: string) => {
  return phone.length >= 8;
};

/**
 * Masks a phone number keeping the first 3 and last 4 digits visible.
 * e.g. "0162342978" → "016xxx2978"
 */
export const maskPhoneNumber = (phone: string): string => {
  if (phone.length <= 7) return phone;
  const visible = 3;
  const tail = 4;
  const masked = phone.length - visible - tail;
  return `${phone.slice(0, visible)}${'x'.repeat(masked)}${phone.slice(-tail)}`;
};

/**
 * Formats dialing code + phone into masked display string.
 * e.g. ("60", "0162342978") → "+60-016xxx2978"
 */
export const formatMaskedPhone = (
  dialingCode?: string,
  mobilePhone?: string,
): string => {
  if (!mobilePhone) return '';
  const masked = maskPhoneNumber(mobilePhone);
  if (!dialingCode) return masked;
  const code = dialingCode.startsWith('+') ? dialingCode : `+${dialingCode}`;
  return `${code}-${masked}`;
};
